import * as PIXI from 'pixi.js';

/**
 * Generic object pool for reusing objects instead of creating/destroying them
 */
export class ObjectPool<T> {
  private pool: T[] = [];
  private createFn: () => T;
  private resetFn: (obj: T) => void;
  private maxSize: number;

  constructor(createFn: () => T, resetFn: (obj: T) => void, initialSize: number = 10, maxSize: number = 1000) {
    this.createFn = createFn;
    this.resetFn = resetFn;
    this.maxSize = maxSize;
    
    // Pre-populate pool
    for (let i = 0; i < initialSize; i++) {
      this.pool.push(this.createFn());
    }
  }

  /**
   * Get an object from the pool
   */
  acquire(): T {
    if (this.pool.length > 0) {
      return this.pool.pop()!;
    }
    return this.createFn();
  }

  /**
   * Return an object to the pool
   */
  release(obj: T): void {
    if (this.pool.length < this.maxSize) {
      this.resetFn(obj);
      this.pool.push(obj);
    }
  }

  /**
   * Get current pool size
   */
  getSize(): number {
    return this.pool.length;
  }

  /**
   * Clear the pool
   */
  clear(): void {
    this.pool = [];
  }
}

/**
 * Sprite pool specifically for PIXI sprites with texture management
 */
export class SpritePool {
  private pools: Map<PIXI.Texture, ObjectPool<PIXI.Sprite>> = new Map();
  
  /**
   * Get a sprite from the pool for a specific texture
   */
  acquire(texture: PIXI.Texture): PIXI.Sprite {
    let pool = this.pools.get(texture);
    if (!pool) {
      pool = new ObjectPool<PIXI.Sprite>(
        () => new PIXI.Sprite(texture),
        (sprite) => {
          sprite.x = 0;
          sprite.y = 0;
          sprite.alpha = 1;
          sprite.scale.set(1, 1);
          sprite.rotation = 0;
          sprite.tint = 0xFFFFFF;
          sprite.visible = true;
        },
        20,
        500
      );
      this.pools.set(texture, pool);
    }
    return pool.acquire();
  }

  /**
   * Return a sprite to the pool
   */
  release(sprite: PIXI.Sprite): void {
    const pool = this.pools.get(sprite.texture);
    if (pool) {
      pool.release(sprite);
    }
  }

  /**
   * Clear all pools
   */
  clear(): void {
    this.pools.clear();
  }
}

/**
 * Graphics pool for PIXI.Graphics objects
 */
export class GraphicsPool extends ObjectPool<PIXI.Graphics> {
  constructor(initialSize: number = 50, maxSize: number = 500) {
    super(
      () => new PIXI.Graphics(),
      (graphics) => {
        graphics.clear();
        graphics.x = 0;
        graphics.y = 0;
        graphics.alpha = 1;
        graphics.scale.set(1, 1);
        graphics.rotation = 0;
        graphics.visible = true;
      },
      initialSize,
      maxSize
    );
  }
}

